import { create } from "zustand";

const initialFilters = {
  search: "",
  appliedSearch: "",
  ordering: "newest",
  appliedOrdering: "newest",
  isActive: "",
  appliedIsActive: "",
  dateFrom: null,
  appliedDateFrom: null,
  dateTo: null,
  appliedDateTo: null,
};

const initialExcelState = {
  excelModalOpen: false,
  excelPreviewData: [],
  excelFileName: "",
};

export const useInventoryStore = create((set, get) => ({
  selectedWarehouseId: null,
  page: 1,
  pageSize: 10,
  totalCount: 0,

  ...initialFilters,
  ...initialExcelState,

  bulkPreviewOpen: false,
  bulkPreviewRows: [],

  savingRows: {},
  rowDrafts: {},
  isExcelModalOpen: false,

  setSelectedWarehouseId: (warehouseId) =>
    set((state) => {
      const nextId = warehouseId ?? null;
      if (String(state.selectedWarehouseId) === String(nextId)) return state;

      return {
        selectedWarehouseId: nextId,
        page: 1,
        rowDrafts: {},
      };
    }),

  setPage: (page) => set({ page }),
  setPageSize: (pageSize) => set({ pageSize, page: 1 }),
  setTotalCount: (totalCount) => set({ totalCount}),

  setSearch: (search) => set({ search }),


  setOrdering: (ordering) => set({ ordering, page: 1 }),
  setIsActive: (isActive) => set({ isActive, page: 1 }),
  setDateFrom: (dateFrom) => set({ dateFrom, page: 1 }),
  setDateTo: (dateTo) => set({ dateTo, page: 1 }),

  setFilters: (payload) =>
    set((state) => ({
      ...state,
      ...payload,
      page: 1,
    })),

  applyFilters: () =>
    set((state) => ({
      appliedSearch: state.search,
      appliedIsActive: state.isActive,
      appliedDateFrom: state.dateFrom,
      appliedDateTo: state.dateTo,
      appliedOrdering: state.ordering,
      page: 1,
    })),

  clearFilters: () =>
    set({
      search: "",
      isActive: "",
      dateFrom: null,
      dateTo: null,
      ordering: "newest",

      appliedSearch: "",
      appliedIsActive: "",
      appliedDateFrom: null,
      appliedDateTo: null,
      appliedOrdering: "newest",
      page: 1,
    }),

  openExcelModal: ({ rows = [], fileName = "" } = {}) =>
    set({
      excelModalOpen: true,
      excelPreviewData: rows,
      excelFileName: fileName,
    }),

  closeExcelModal: () =>
    set({
      excelModalOpen: false,
    }),

  setExcelPreviewData: (rows) =>
    set({
      excelPreviewData: Array.isArray(rows) ? rows : [],
    }),

  setExcelFileName: (fileName) =>
    set({
      excelFileName: fileName || "",
    }),

  resetExcelState: () =>
    set({
      ...initialExcelState,
    }),

  openBulkPreview: () =>
    set({
      bulkPreviewOpen: true,
    }),

  closeBulkPreview: () =>
    set({
      bulkPreviewOpen: false,
    }),

  setBulkPreviewRows: (rows) =>
    set({
      bulkPreviewRows: Array.isArray(rows) ? rows : [],
    }),

  setRowSaving: (productId, status = true) =>
    set((state) => ({
      savingRows: {
        ...state.savingRows,
        [productId]: status,
      },
    })),

  clearRowSaving: (productId) =>
    set((state) => {
      const next = { ...state.savingRows };
      delete next[productId];
      return { savingRows: next };
    }),

  clearAllSavingRows: () =>
    set((state) =>
      Object.keys(state.savingRows).length === 0 ? state : { savingRows: {} }
    ),

  // -----------------------------
// Row Drafts
// -----------------------------
setRowDraft: (productId, draft) =>
  set((state) => ({
    rowDrafts: {
      ...state.rowDrafts,
      [productId]: draft,
    },
  })),

patchRowDraft: (productId, partialDraft) =>
  set((state) => ({
    rowDrafts: {
      ...state.rowDrafts,
      [productId]: {
        ...(state.rowDrafts[productId] || {}),
        ...partialDraft,
      },
    },
  })),

updateRowDraftField: (productId, field, value) =>
  set((state) => {
    const currentDraft = state.rowDrafts[productId] || {};

    return {
      rowDrafts: {
        ...state.rowDrafts,
        [productId]: {
          ...currentDraft,
          [field]: value,
          isDirty: true,
        },
      },
    };
  }),

resetRowDraft: (productId) =>
  set((state) => {
    const currentDraft = state.rowDrafts[productId];
    if (!currentDraft) return state;

    return {
      rowDrafts: {
        ...state.rowDrafts,
        [productId]: {
          ...currentDraft,
          ...currentDraft.originalData,
          isDirty: false,
        },
      },
    };
  }),

markRowSaved: (productId, updatedFields = {}) =>
  set((state) => {
    const currentDraft = state.rowDrafts[productId];
    if (!currentDraft) return state;

    const nextOriginalData = {
      ...(currentDraft.originalData || {}),
      ...updatedFields,
    };

    return {
      rowDrafts: {
        ...state.rowDrafts,
        [productId]: {
          ...currentDraft,
          ...updatedFields,
          originalData: nextOriginalData,
          isDirty: false,
        },
      },
    };
  }),

removeRowDraft: (productId) =>
  set((state) => {
    if (!(productId in state.rowDrafts)) return state;
    const next = { ...state.rowDrafts };
    delete next[productId];
    return { rowDrafts: next };
  }),

resetAllDrafts: () =>
  set((state) =>
    Object.keys(state.rowDrafts).length === 0 ? state : { rowDrafts: {} }
  ),

replaceRowDrafts: (drafts) =>
  set((state) => {
    const nextDrafts = drafts || {};
    const prevStr = JSON.stringify(state.rowDrafts);
    const nextStr = JSON.stringify(nextDrafts);

    if (prevStr === nextStr) return state;

    return {
      rowDrafts: nextDrafts,
    };
  }),


  getInventoryQueryParams: () => {
    const state = get();
    return {
      warehouseId: state.selectedWarehouseId,
      search: state.appliedSearch,
      page: state.page,
      pageSize: state.pageSize,
      ordering: state.ordering,
      isActive: state.isActive,
      dateFrom: state.dateFrom,
      dateTo: state.dateTo,
    };
  },
}));
